/**
 * ProductionCalendar — month-view grid that lays out freeze-dry batches
 * on real dates so operators can see collisions at a glance.
 *
 * Inputs:
 *   batches:      integer, total batches to run
 *   cycle_hours:  hours per cycle (from the machine picker)
 *   start_date:   ISO date string; defaults to today
 *   labels:       optional array of strings — one per batch (falls back
 *                 to "Batch #N" when missing)
 *
 * Renders:
 *   • A month header with prev / next navigation
 *   • A 7-column weekday grid, 6 rows (max), Monday-first for both US + CA
 *     conventions matching Walmart-baseline audiences
 *   • Batch pills colored by whether they fit inside one calendar day or
 *     overflow to the next (>24 h cycles) — collisions are visually clear
 *   • A footer summary: total production span, batches per week
 */

import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Zap, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';

const DAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function addHours(date, h) {
  const d = new Date(date);
  d.setTime(d.getTime() + h * 3600 * 1000);
  return d;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}

function startOfMonth(d) {
  const s = new Date(d.getFullYear(), d.getMonth(), 1);
  s.setHours(0, 0, 0, 0);
  return s;
}

function startOfMondayWeek(d) {
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  // JS: Sunday = 0, Monday = 1 … Sunday should map to 6 in Monday-first.
  const wd = (s.getDay() + 6) % 7;
  s.setDate(s.getDate() - wd);
  return s;
}

// Distribute batches consecutively starting at start_date. Each batch
// occupies `cycle_hours` real hours plus a fixed 1-h changeover, then
// the next batch begins. Returns an array of {label, start, end} rows.
function scheduleBatches(startDate, batches, cycleH) {
  const rows = [];
  let cursor = new Date(startDate);
  cursor.setHours(8, 0, 0, 0);       // start production days at 08:00
  for (let i = 0; i < batches; i++) {
    const start = new Date(cursor);
    const end = addHours(start, cycleH);
    rows.push({ index: i + 1, start, end });
    cursor = addHours(end, 1);        // 1-h changeover
  }
  return rows;
}

export default function ProductionCalendar({
  batches, cycle_hours, start_date, labels = [],
}) {
  const totalBatches = Number(batches) || 0;
  const cycleH       = Number(cycle_hours) || 28;
  const startDate    = start_date ? new Date(start_date) : new Date();

  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(startDate));

  const rows = useMemo(
    () => scheduleBatches(startDate, totalBatches, cycleH),
    [startDate, totalBatches, cycleH],
  );

  // Group batches by day for quick lookup while rendering.
  const byDay = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.start.toDateString();
      const list = map.get(key) || [];
      list.push(r);
      map.set(key, list);
    }
    return map;
  }, [rows]);

  // Build the 6-week grid (42 cells) starting on the Monday before the
  // first of the month. Each cell is a Date.
  const gridStart = startOfMondayWeek(monthAnchor);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(d.getDate() + i);
    cells.push(d);
  }

  const monthLabel = monthAnchor.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  const totalHours = totalBatches * cycleH + Math.max(0, totalBatches - 1);
  const productionDays = totalHours / 24;
  const batchesPerWeek = totalBatches > 0
    ? (totalBatches / Math.max(1, productionDays / 7)).toFixed(1)
    : 0;

  const prevMonth = () => {
    const d = new Date(monthAnchor);
    d.setMonth(d.getMonth() - 1);
    setMonthAnchor(d);
  };
  const nextMonth = () => {
    const d = new Date(monthAnchor);
    d.setMonth(d.getMonth() + 1);
    setMonthAnchor(d);
  };

  return (
    <div className="space-y-3" data-testid="fp-production-calendar">
      <div className="flex items-center justify-between gap-2">
        <Button type="button" variant="outline" size="sm" onClick={prevMonth} data-testid="fp-cal-prev">
          <ChevronLeft className="w-4 h-4 mr-1" /> Prev
        </Button>
        <div className="text-sm font-bold" data-testid="fp-cal-month">{monthLabel}</div>
        <Button type="button" variant="outline" size="sm" onClick={nextMonth} data-testid="fp-cal-next">
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        {DAYS_SHORT.map((d) => (<div key={d} className="text-center py-1 font-semibold">{d}</div>))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, idx) => {
          const isThisMonth = d.getMonth() === monthAnchor.getMonth();
          const dayBatches = byDay.get(d.toDateString()) || [];
          const isToday = sameDay(d, new Date());
          const collisions = dayBatches.length > 1;
          return (
            <div
              key={idx}
              className={
                'min-h-[70px] rounded border p-1 text-[10px] flex flex-col gap-0.5 ' +
                (isThisMonth
                  ? 'bg-card border-border'
                  : 'bg-muted/30 border-transparent text-muted-foreground/60') +
                (isToday ? ' ring-2 ring-primary/60' : '') +
                (collisions ? ' bg-amber-500/10 border-amber-500/50' : '')
              }
              data-testid={`fp-cal-day-${d.toISOString().slice(0,10)}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono">{d.getDate()}</span>
                {collisions && <AlertTriangle className="w-3 h-3 text-amber-600" />}
              </div>
              {dayBatches.map((b) => {
                const overflow = !sameDay(b.start, b.end);
                return (
                  <div
                    key={b.index}
                    className={
                      'rounded px-1 py-0.5 text-[9px] font-semibold border ' +
                      (overflow
                        ? 'bg-orange-500/20 border-orange-500/60 text-orange-800 dark:text-orange-200'
                        : 'bg-primary/15 border-primary/40 text-primary')
                    }
                    title={`${labels[b.index - 1] || `Batch #${b.index}`} · ${b.start.toLocaleString()} → ${b.end.toLocaleString()}`}
                    data-testid={`fp-cal-batch-${b.index}`}
                  >
                    <Zap className="inline w-2.5 h-2.5 mr-0.5" />
                    {labels[b.index - 1] || `Batch #${b.index}`}
                    {overflow ? ' ▷' : ''}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-1 border-t border-border">
        <span><strong className="text-foreground">{totalBatches}</strong> batches</span>
        <span><strong className="text-foreground">{productionDays.toFixed(1)}</strong> continuous days</span>
        <span><strong className="text-foreground">{batchesPerWeek}</strong> batches / week (avg)</span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-primary/40 border border-primary/60" />
          Fits one day
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-orange-500/40 border border-orange-500/60" />
          Runs overnight
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-amber-500/20 border border-amber-500/60" />
          Collision — multiple batches same day
        </span>
      </div>
    </div>
  );
}
